import React, { useState, useEffect } from "react";
import { Offcanvas } from "react-bootstrap";
import { IoIosArrowBack } from "react-icons/io";
import {
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ExpandMore, Close } from "@mui/icons-material";
import { getBudgetTypeInfo } from "../../../../utils/budgetTypeUtils";

const selectSx = {
  height: "38px",
  backgroundColor: "#fff",
};

const menuProps = {
  PaperProps: {
    sx: { maxHeight: 280 },
  },
};

const AwaitingAllocationFilter = ({
  show,
  onClose,
  projectList = [],
  sourceList = [],
  statusFilter = "All statuses",
  projectFilter = "All projects",
  modelFilter = "All models",
  sourceFilter = "All sources",
  ageFilter = "All ages",
  onApply,
  onClear,
  isLoading = false,
}) => {
  const [drawerStatus, setDrawerStatus] = useState(statusFilter);
  const [drawerProject, setDrawerProject] = useState(projectFilter);
  const [drawerModel, setDrawerModel] = useState(modelFilter);
  const [drawerSource, setDrawerSource] = useState(sourceFilter);
  const [drawerAge, setDrawerAge] = useState(ageFilter);

  // Sync draft states when drawer opens
  useEffect(() => {
    if (show) {
      setDrawerStatus(statusFilter || "All statuses");
      setDrawerProject(projectFilter || "All projects");
      setDrawerModel(modelFilter || "All models");
      setDrawerSource(sourceFilter || "All sources");
      setDrawerAge(ageFilter || "All ages");
    }
  }, [show, statusFilter, projectFilter, modelFilter, sourceFilter, ageFilter]);

  const handleClear = () => {
    setDrawerStatus("All statuses");
    setDrawerProject("All projects");
    setDrawerModel("All models");
    setDrawerSource("All sources");
    setDrawerAge("All ages");
    if (onClear) {
      onClear();
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (onApply) {
      onApply({
        status: drawerStatus,
        project_id: drawerProject,
        model: drawerModel,
        source: drawerSource,
        age: drawerAge,
      });
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      style={{ width: window.innerWidth <= 768 ? "80%" : "50%" }}
    >
      {/* Top Header */}
      <div className="d-flex align-items-center justify-content-between filter-h-bg px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="R_btn"
        >
          <div className="d-flex text-rgba-gray align-items-center">
            <IoIosArrowBack />
            Filter
          </div>
        </button>
        <div>
          <button
            type="button"
            onClick={handleClear}
            className="R_btn tex-tgba-red clear-filter-btn"
          >
            Clear Filter
          </button>
        </div>
      </div>

      {/* Drawer Body: Form */}
      <Offcanvas.Body className="p-0 position-relative">
        <form
          onSubmit={handleSubmit}
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Scrollable Form Body */}
          <div
            className="bg-white px-4 py-3"
            style={{ flex: 1, overflowY: "auto", paddingBottom: "90px" }}
          >
            <div className="row g-3">
              {/* 1. Project Filter (Full width) */}
              <div className="col-12 col-md-12">
                <label className="form-label">
                  Project
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={drawerProject}
                    onChange={(e) => setDrawerProject(e.target.value)}
                    IconComponent={ExpandMore}
                    size="small"
                    sx={selectSx}
                    MenuProps={menuProps}
                    renderValue={(selected) => {
                      if (!selected || selected === "All projects") {
                        return "All projects";
                      }
                      const found = projectList.find(
                        (p) => String(p.project_id || p.id) === String(selected)
                      );
                      const title =
                        found?.project_title ||
                        found?.title ||
                        found?.project_name ||
                        found?.name ||
                        `Project #${selected}`;
                      const budgetInfo = getBudgetTypeInfo(found?.budget_type);

                      return (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            paddingRight:
                              drawerProject && drawerProject !== "All projects"
                                ? "24px"
                                : "6px",
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginRight: "12px",
                            }}
                          >
                            {title}
                          </span>
                          {budgetInfo.badgeLabel && (
                            <span
                              className={budgetInfo.badgeClass}
                              style={{ flexShrink: 0 }}
                            >
                              {budgetInfo.badgeLabel}
                            </span>
                          )}
                        </div>
                      );
                    }}
                    endAdornment={
                      drawerProject && drawerProject !== "All projects" ? (
                        <IconButton
                          size="small"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDrawerProject("All projects");
                          }}
                          sx={{
                            position: "absolute",
                            right: "28px",
                            padding: "2px",
                            color: "#9ca3af",
                            "&:hover": { color: "#4b5563" },
                          }}
                          title="Clear project"
                        >
                          <Close sx={{ fontSize: "16px" }} />
                        </IconButton>
                      ) : null
                    }
                  >
                    <MenuItem value="All projects">All projects</MenuItem>
                    {projectList.map((p) => {
                      const pId = String(p.project_id || p.id || "");
                      const pName =
                        p.project_title ||
                        p.title ||
                        p.project_name ||
                        p.name ||
                        `Project #${pId}`;
                      const budgetInfo = getBudgetTypeInfo(p.budget_type);
                      return (
                        <MenuItem
                          key={pId}
                          value={pId}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            py: 1,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              marginRight: "8px",
                            }}
                          >
                            {pName}
                          </span>
                          {budgetInfo.badgeLabel && (
                            <span
                              className={budgetInfo.badgeClass}
                              style={{ flexShrink: 0 }}
                            >
                              {budgetInfo.badgeLabel}
                            </span>
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>

              {/* 2. Model Filter (md-6) */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Model
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={drawerModel}
                    onChange={(e) => setDrawerModel(e.target.value)}
                    IconComponent={ExpandMore}
                    size="small"
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="All models">All models</MenuItem>
                    <MenuItem value="Fixed">Fixed Cost</MenuItem>
                    <MenuItem value="Contract">Contract</MenuItem>
                    <MenuItem value="Time_and_m">T&M(Contract)</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* 3. Source Filter (md-6) */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Source
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={drawerSource}
                    onChange={(e) => setDrawerSource(e.target.value)}
                    IconComponent={ExpandMore}
                    size="small"
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="All sources">All sources</MenuItem>
                    {sourceList.map((s) => {
                      const val =
                        typeof s === "string" ? s : s.source || s.name || s.id;
                      return (
                        <MenuItem key={val} value={val}>
                          {val}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>

              {/* 4. Status Filter (md-6) */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Status
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={drawerStatus}
                    onChange={(e) => setDrawerStatus(e.target.value)}
                    IconComponent={ExpandMore}
                    size="small"
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="All statuses">All statuses</MenuItem>
                    <MenuItem value="unmapped">Unmapped</MenuItem>
                    <MenuItem value="mapped">Mapped</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* 5. Age Filter (md-6) */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Age (Receipts)
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={drawerAge}
                    onChange={(e) => setDrawerAge(e.target.value)}
                    IconComponent={ExpandMore}
                    size="small"
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="All ages">All ages</MenuItem>
                    <MenuItem value="0-30 days">0–30 days</MenuItem>
                    <MenuItem value="31-60 days">31–60 days</MenuItem>
                    <MenuItem value="Over 60 days">Over 60 days</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          {/* Fixed Bottom Footer matching PMS drawer style */}
          <div className="bg-white position-absolute w-100 bottom-0 py-3 px-4">
            <div className="d-flex justify-content-start align-items-center">
              <button
                type="submit"
                className="add_btn me-3"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="d-flex align-items-center gap-1">
                    <CircularProgress size={14} color="inherit" />
                    <span>Applying...</span>
                  </div>
                ) : (
                  "Apply"
                )}
              </button>
              <button
                type="button"
                className="cancel_btn"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AwaitingAllocationFilter;
